import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Send, Trash2, Save, Clock, Download, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { getDrafts, updateDraft, removeDraft } from '../../lib/offline';
import { UPLOAD_TIMEOUT_MS } from '../../lib/photo';
import { ZoomableImage } from '../../components/ZoomableImage';

const LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const CYCLE_MS = 60 * 60 * 1000;

const fmtRemain = (ms) => {
  const m = Math.floor(Math.abs(ms) / 60000);
  const s = Math.floor((Math.abs(ms) % 60000) / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function MobileDrafts() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState(null);
  const [edits, setEdits] = useState({});
  const [now, setNow] = useState(Date.now());
  const [sending, setSending] = useState(false);
  const [pinned, setPinned] = useState(null);
  const [preFocus, setPreFocus] = useState({});

  const pinnedDraft = useMemo(() => (drafts || []).find(d => d.id === pinned) || null, [drafts, pinned]);
  const pinnedUrl = useMemo(() => (pinnedDraft?.photoBlob ? URL.createObjectURL(pinnedDraft.photoBlob) : null), [pinnedDraft]);
  useEffect(() => () => { if (pinnedUrl) URL.revokeObjectURL(pinnedUrl); }, [pinnedUrl]);

  const downloadDraft = (d) => {
    const dt = new Date(d.capturedAt);
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}_${pad(dt.getHours())}${pad(dt.getMinutes())}`;
    const name = `${(d.stationName || 'foto').replace(/[^\w\u00C0-\u017F-]+/g, '_').replace(/_+/g, '_')}_${stamp}.jpg`;
    const url = URL.createObjectURL(d.photoBlob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success('Foto descargada');
  };

  const load = () => getDrafts().then(setDrafts);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const start = useMemo(() => {
    if (!drafts?.length) return null;
    return Math.min(...drafts.map(d => new Date(d.capturedAt).getTime()));
  }, [drafts]);
  const remaining = start != null ? start + CYCLE_MS - now : null;
  const expired = remaining != null && remaining <= 0;

  const setPrice = (id, fid, raw) => {
    let x = String(raw).replace(',', '.').replace(/[^\d.]/g, '');
    const i = x.indexOf('.');
    if (i !== -1) x = x.slice(0, i + 1) + x.slice(i + 1).replace(/\./g, '');
    const m = x.match(/^(\d{0,3})(\.\d{0,2})?/);
    const v = m ? (m[1] || '') + (m[2] || '') : '';
    setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), [fid]: v } }));
  };

  const saveOne = async (d) => {
    const patch = edits[d.id];
    if (!patch) { toast('Sin cambios'); return; }
    const merged = { ...d.prices };
    for (const [fid, v] of Object.entries(patch)) merged[fid] = +(parseFloat(v) || 0).toFixed(2);
    const bad = Object.entries(merged).find(([, v]) => Number(v) !== 0 && (Number(v) < 10 || Number(v) > 100));
    if (bad) { toast.error(`Precio fuera de rango en ${(LABELS[bad[0]] || bad[0])}`, { description: 'Entre Q10 y Q100 · usa 0 si no hay dato.' }); return; }
    await updateDraft(d.id, { prices: merged });
    setEdits(e => { const n = { ...e }; delete n[d.id]; return n; });
    toast.success('Borrador guardado');
    load();
  };

  const discard = async (d) => {
    await removeDraft(d.id);
    toast('Borrador descartado');
    load();
  };

  const sendAll = async () => {
    if (!drafts?.length) return;
    if (Object.keys(edits).length) { toast.error('Tienes precios sin Guardar', { description: 'Guarda cada tarjeta editada antes de enviar.' }); return; }
    setSending(true);
    // Estación propia primero, luego por hora de captura
    const ordered = [...drafts].sort((a, b) => (b.isOwn ? 1 : 0) - (a.isOwn ? 1 : 0) || (a.capturedAt || '').localeCompare(b.capturedAt || ''));
    let ok = 0, fail = 0;
    for (const d of ordered) {
      try {
        const fd = new FormData();
        fd.append('station_id', d.stationId);
        fd.append('service_type', d.serviceType);
        fd.append('prices', JSON.stringify(d.prices || {}));
        fd.append('lat', d.lat);
        fd.append('lng', d.lng);
        fd.append('notes', d.notes || '');
        fd.append('no_gps', d.noGps ? 'true' : 'false');
        fd.append('queued', 'true');
        fd.append('captured_at_client', d.capturedAt);
        fd.append('photo', new File([d.photoBlob], d.photoName || 'foto.jpg', { type: d.photoType || 'image/jpeg' }));
        await api.post('/prices', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS });
        await removeDraft(d.id);
        ok += 1;
      } catch (e) {
        fail += 1;
        const det = e.response?.data?.detail;
        toast.error(`${d.stationName}: no se envió`, { description: typeof det === 'string' ? det : 'Reintenta en unos minutos.', duration: 8000 });
      }
    }
    setSending(false);
    if (ok > 0) toast.success(`${ok} captura(s) enviadas al tracking${fail ? ` · ${fail} pendiente(s)` : ''}`);
    load();
    if (fail === 0 && ok > 0) navigate('/app/history');
  };

  if (drafts === null) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="px-5 py-5 fade-in pb-32" data-testid="drafts-page">
      {pinnedDraft && pinnedUrl && (
        <div className="sticky top-0 z-40 -mx-5 px-3 pt-2 pb-2 bg-[#f8f6f1]/95 backdrop-blur-md border-b border-[#e8e5de] shadow-sm" data-testid="pinned-photo-panel">
          <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
            <p className="text-[11.5px] font-medium truncate">{pinnedDraft.stationName} · foto {new Date(pinnedDraft.capturedAt).toLocaleTimeString('es-GT', { timeStyle: 'short' })}</p>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => downloadDraft(pinnedDraft)} className="w-8 h-8 rounded-full bg-white border border-[#d4cfc4] flex items-center justify-center" title="Descargar foto" data-testid="pinned-photo-download"><Download size={14}/></button>
              <button onClick={() => setPinned(null)} className="w-8 h-8 rounded-full bg-[#1c1c1c] text-white flex items-center justify-center" title="Cerrar foto" data-testid="pinned-photo-close"><X size={14}/></button>
            </div>
          </div>
          <ZoomableImage src={pinnedUrl} alt={pinnedDraft.stationName} maxH="38vh" testId="pinned-photo-zoom" onDownload={() => downloadDraft(pinnedDraft)}/>
        </div>
      )}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[12px] text-[#5a5a5a] mb-4"><ArrowLeft size={14}/> Volver</button>
      <h1 className="font-serif text-[26px] leading-tight tracking-tight">Por enviar</h1>
      <p className="text-[12.5px] text-[#5a5a5a] mt-1">Edita los precios de tus fotos en sitio, guarda y envía el tracking completo. Toca la foto de un borrador para anclarla arriba mientras digitas.</p>

      {drafts.length === 0 ? (
        <p className="text-[13px] text-[#8a8579] mt-8 text-center">No hay capturas por enviar. Usa "Guardar borrador" al capturar.</p>
      ) : (
        <>
          <div className={`mt-4 rounded-xl p-4 text-center ${expired ? 'bg-[#dc2626] text-white' : 'bg-[#0f1d6f] text-white'}`} data-testid="drafts-countdown">
            <p className="text-[11px] uppercase tracking-wider opacity-90 flex items-center justify-center gap-1.5"><Clock size={12}/> Ciclo de tracking (1 h)</p>
            <p className="text-[26px] font-bold font-mono tabular-nums">{expired ? `Vencido hace ${fmtRemain(remaining)}` : `Quedan ${fmtRemain(remaining)}`}</p>
            {expired && <p className="text-[11.5px] mt-1">El ciclo de 1 hora ya venció · envía cuanto antes</p>}
          </div>

          <div className="mt-4 space-y-3">
            {drafts.map(d => {
              const fuels = ORDER.filter(f => d.prices && f in d.prices);
              const edited = !!edits[d.id];
              return (
                <div key={d.id} className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid={`draft-${d.id}`}>
                  <div className="flex items-start gap-3">
                    {d.photoBlob && (
                      <button onClick={() => setPinned(pinned === d.id ? null : d.id)} className="shrink-0 relative" title="Ver foto anclada" data-testid={`draft-photo-${d.id}`}>
                        <img src={URL.createObjectURL(d.photoBlob)} alt="" className={`w-14 h-14 object-cover rounded-lg ${pinned === d.id ? 'ring-2 ring-[#0f1d6f]' : ''}`}/>
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-medium truncate">{d.stationName} {d.isOwn && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#e0e7ff] text-[#3730a3] uppercase">Propia</span>}</p>
                      <p className="text-[11px] text-[#8a8579]">{d.brand} · {d.serviceType === 'full' ? 'Servicio completo' : 'Autoservicio'} · foto {new Date(d.capturedAt).toLocaleTimeString('es-GT', { timeStyle: 'short' })}</p>
                    </div>
                    <button onClick={() => downloadDraft(d)} className="text-[#0f1d6f] p-1" title="Descargar foto" data-testid={`draft-download-${d.id}`}><Download size={15}/></button>
                    <button onClick={() => discard(d)} className="text-[#991b1b] p-1" title="Descartar" data-testid={`draft-discard-${d.id}`}><Trash2 size={15}/></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {fuels.map(fid => (
                      <label key={fid} className="text-[10px] uppercase tracking-wider text-[#8a8579]">
                        {LABELS[fid] || fid}
                        <input
                          inputMode="decimal"
                          value={edits[d.id]?.[fid] ?? (d.prices[fid] || '')}
                          placeholder={preFocus[`${d.id}-${fid}`] != null ? String(preFocus[`${d.id}-${fid}`]) : ''}
                          onFocus={() => {
                            const cur = edits[d.id]?.[fid] ?? (d.prices[fid] || '');
                            if (cur !== '' && cur != null) {
                              setPreFocus(p => ({ ...p, [`${d.id}-${fid}`]: cur }));
                              setEdits(e => ({ ...e, [d.id]: { ...(e[d.id] || {}), [fid]: '' } }));
                            }
                          }}
                          onChange={e => setPrice(d.id, fid, e.target.value)}
                          onBlur={e => {
                            const k = `${d.id}-${fid}`;
                            const prev = preFocus[k];
                            setPreFocus(p => { const n = { ...p }; delete n[k]; return n; });
                            if (e.target.value === '' && prev != null) {
                              setEdits(ed => ({ ...ed, [d.id]: { ...(ed[d.id] || {}), [fid]: prev } }));
                            }
                          }}
                          className="mt-0.5 w-full h-9 text-center font-mono tabular-nums text-[14px] bg-[#f8f6f1] border border-[#d4cfc4] rounded-lg focus:border-[#1c1c1c] focus:bg-white focus:outline-none"
                          data-testid={`draft-price-${d.id}-${fid}`}
                        />
                      </label>
                    ))}
                  </div>
                  {edited && (
                    <Button onClick={() => saveOne(d)} className="mt-3 w-full h-9 rounded-full bg-[#1c1c1c] text-white text-[12.5px]" data-testid={`draft-save-${d.id}`}>
                      <Save size={13} className="mr-1.5"/> Guardar
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <Button onClick={sendAll} disabled={sending} className="mt-5 w-full h-12 rounded-full bg-[#15803d] hover:bg-[#166534] text-white text-[14px]" data-testid="drafts-send-all">
            {sending ? <Loader2 size={15} className="animate-spin mr-2"/> : <Send size={15} className="mr-2"/>}
            Enviar tracking ({drafts.length})
          </Button>
        </>
      )}
    </div>
  );
}
